import {
  format,
  formatISO,
  add,
  sub,
  parseISO,
  isBefore,
  isAfter,
  isValid,
  isEqual,
  isSameDay,
  isDate,
  startOfDay,
  startOfToday,
  addMinutes,
  endOfDay,
  differenceInMilliseconds,
  differenceInSeconds,
  differenceInMinutes,
  differenceInHours,
  differenceInDays,
  getHours,
} from 'date-fns';
import { utcToZonedTime, zonedTimeToUtc } from 'date-fns-tz';
import { es, enUS, enGB, fr, it, de, pl, pt, nl, enAU, da, nb, sv, tr } from 'date-fns/locale';

const DATE_TIME_FORMATS = {
  year_month_day: 'yyyy-MM-dd',
  weekday_month_day: 'EEE, MMM dd',
  weekday_month_day_year: 'EEE, MMM dd, yyyy',
  weekday_day_month_year_no_comma: 'EEE d MMM yyyy',
  weekday_month_day_year_am_pm_lower_case: 'EEE, MMM dd, yyyy - h:mmaaa',
  weekday_month_day_year_time_24hr_lower_case: 'EEE, MMM dd, yyyy - HH:mm',
  weekday_day_month_year: 'E dd MMM yyyy',
  month_day_year: 'MMM dd, yyyy',
  month_day_full_year_slash: 'MM/dd/yyyy',
  month_day_full_year_slash_spaced: 'MM / dd / yyyy',
  year_month_day_time: 'yyyy-MM-dd HH:mm',
  year_month_day_time_am_pm: 'yyyy-MM-dd HH:mm aa',
  time_24hr: 'HH:mm',
  time: 'hh:mmaa',
  time_lower_case: 'h:mmaaa',
  day_month_year: 'dd MMM, yyyy',
  day_month_year_no_comma: 'dd MMM yyyy',
  day_month_year_in_numbers: 'dd-MM-yyyy',
  day_month_year_day_time_am_pm: 'dd MMM yyyy, hh:mmaa',
  day_month_year_day_time_am_pm_lower_case: 'dd MMM yyyy, h:mmaaa',
  day_month_year_day_time_24h_lower_case: 'dd MMM yyyy, HH:mm',
  day_month_year_slash: 'dd/MM/yy',
  day_month_full_year_slash: 'dd/MM/yyyy',
  day_month_full_year_slash_spaced: 'dd / MM / yyyy',
  day_month_shorthand_year: 'd MMM yyyy',
  full_month_day_year: 'MMMM dd, yyyy',
  full_day_month_year: 'dd MMMM, yyyy',
  day_full_month_year_no_comma: 'dd MMMM yyyy',
  month_day: 'MMM dd',
  year_month_day_utc: "yyyy-MM-dd'T'HH:mm:ss'Z'",
  year_month_day_UTC: "yyyy-MM-dd'T'HH:mm:ss'",
} as const;

const Locales = {
  'es-ES': es,
  'en-US': enUS,
  'en-GB': enGB,
  'fr-FR': fr,
  'it-IT': it,
  'de-DE': de,
  'pl-PL': pl,
  'pt-PT': pt,
  'nl-NL': nl,
  'en-AU': enAU,
  'da-DK': da,
  'nb-NO': nb,
  'sv-SE': sv,
  'tr-TR': tr,
} as const;

/** Returns the local time zone */
function guessTimezone() {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

type Locale = keyof typeof Locales;

function formatWithLocale(
  date: Date | number,
  str: string,
  locale: string
): string {
  return format(date, str, { locale: Locales[locale as Locale] });
}

function convertTimeStringToObject(timeString: string) {
  const [hours, minutes] = timeString.split(':').map(Number);
  const period = hours >= 12 ? 'PM' : 'AM';
  const convertedHours = hours % 12 || 12;
  function num2text(num: number | string) {
    return String(num).substring(0, 2).padStart(2, '0');
  }
  return {
    name: `${num2text(convertedHours)}:${num2text(minutes)}${period}`,
    value: `${num2text(hours)}:${num2text(minutes)}`,
  };
}

/**
 * Normalises a date value to YYYY-MM-DD format string
 * Handles ISO strings, Date objects, and already formatted strings
 * @param date - Date value to normalise (string, Date, or undefined)
 * @returns Formatted date string in YYYY-MM-DD format, or empty string if invalid
 */
function normaliseDateToString(date: string | Date | undefined): string {
  if (!date) return '';

  try {
    if (isDate(date) && isValid(date)) {
      return format(date as Date, DATE_TIME_FORMATS.year_month_day);
    }

    if (typeof date === 'string') {
      // If already in YYYY-MM-DD format, return as is
      if (/^\d{4}-\d{2}-\d{2}$/.test(date)) {
        return date;
      }

      const parsed = parseISO(date);
      if (isValid(parsed)) {
        return format(parsed, DATE_TIME_FORMATS.year_month_day);
      }
    }

    return '';
  } catch {
    return '';
  }
}

/**
 * Calculates the number of days between two dates (trip duration)
 * @param startDate - The start date (Date object or string)
 * @param endDate - The end date (Date object or string)
 * @returns The number of days between the dates (rounded up), or null if either date is missing or invalid
 */
function getTripDurationInDays(
  startDate: Date | string | undefined | null,
  endDate: Date | string | undefined | null
): number | null {
  if (!startDate || !endDate) {
    return null;
  }

  try {
    const start = new Date(startDate);
    const end = new Date(endDate);
    if (!isValid(start) || !isValid(end)) {
      return null;
    }

    const diffTime = Math.abs(end.getTime() - start.getTime());
    // Add 1 to include both start and end dates as trip days
    return Math.floor(diffTime / (1000 * 60 * 60 * 24)) + 1;
  } catch {
    return null;
  }
}

export {
  format,
  formatWithLocale,
  formatISO,
  add,
  sub,
  parseISO,
  utcToZonedTime,
  isValid,
  isBefore,
  isAfter,
  isEqual,
  startOfDay,
  startOfToday,
  addMinutes,
  endOfDay,
  isSameDay,
  differenceInMilliseconds,
  differenceInSeconds,
  differenceInMinutes,
  differenceInHours,
  differenceInDays,
  getHours,
  isDate,
  guessTimezone,
  zonedTimeToUtc,
  DATE_TIME_FORMATS,
  convertTimeStringToObject,
  normaliseDateToString,
  getTripDurationInDays,
};
